/**
 * Session-scoped cache for the-core ad + advertiser payloads. Provider
 * lives under the /intelligence route so cache lifetime = "user is in
 * the intelligence module on this space". On space change the parent
 * dashboard subtree remounts and the cache naturally resets.
 *
 * Cache + in-flight maps are stored in refs (not state) so writes don't
 * re-render the whole subtree. Consumers await the helpers and update
 * their own local state when payloads land.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import * as API from '@/services/api.js'


const IntelligenceContext = createContext(null)


export const IntelligenceProvider = ({ children }) => {

    // Ad payload cache + in-flight dedupe.
    const adsCacheRef = useRef(new Map())
    const adsInFlightRef = useRef(new Map())

    // Advertiser payload cache + in-flight dedupe.
    const advertisersCacheRef = useRef(new Map())
    const advertisersInFlightRef = useRef(new Map())

    // Cross-brand ad-feed seed. Lives in provider state so it:
    //   - persists while navigating between intelligence pages (Library →
    //     Brand → BoardDetail → back to Library) — back-nav stays on the
    //     same shuffle, scroll positions/state restore correctly
    //   - resets on page refresh (provider remounts → new seed)
    //   - resets when the user leaves /intelligence and comes back
    //     (provider unmounts on leave, fresh mount on return)
    const [feedSeed, setFeedSeed] = useState(() => Math.random().toString(36).slice(2, 10))
    const shuffleFeedSeed = useCallback(() => {
        setFeedSeed(Math.random().toString(36).slice(2, 10))
    }, [])

    const getAd = useCallback(async (adId, options = {}) => {
        if (!adId) {
            return null
        }
        const fresh = options.fresh === true
        if (!fresh && adsCacheRef.current.has(adId)) {
            return adsCacheRef.current.get(adId)
        }
        if (!fresh && adsInFlightRef.current.has(adId)) {
            return adsInFlightRef.current.get(adId)
        }
        const promise = (async () => {
            try {
                const { data } = await API.getTheCoreAd(adId, { engagement: options.engagement })
                adsCacheRef.current.set(adId, data)
                return data
            } finally {
                adsInFlightRef.current.delete(adId)
            }
        })()
        adsInFlightRef.current.set(adId, promise)
        return promise
    }, [])

    const getAdvertiser = useCallback(async (advertiserId, options = {}) => {
        if (!advertiserId) {
            return null
        }
        const fresh = options.fresh === true
        if (!fresh && advertisersCacheRef.current.has(advertiserId)) {
            return advertisersCacheRef.current.get(advertiserId)
        }
        if (!fresh && advertisersInFlightRef.current.has(advertiserId)) {
            return advertisersInFlightRef.current.get(advertiserId)
        }
        const promise = (async () => {
            try {
                const { data } = await API.getTheCoreAdvertiser(advertiserId, { engagement: options.engagement })
                advertisersCacheRef.current.set(advertiserId, data)
                return data
            } finally {
                advertisersInFlightRef.current.delete(advertiserId)
            }
        })()
        advertisersInFlightRef.current.set(advertiserId, promise)
        return promise
    }, [])

    const invalidateAd = useCallback((adId) => {
        if (!adId) {
            return
        }
        adsCacheRef.current.delete(adId)
        adsInFlightRef.current.delete(adId)
    }, [])

    const invalidateAdvertiser = useCallback((advertiserId) => {
        if (!advertiserId) {
            return
        }
        advertisersCacheRef.current.delete(advertiserId)
        advertisersInFlightRef.current.delete(advertiserId)
        // Drop every cached ad belonging to this advertiser — they'll be
        // refetched as the user navigates back into the brand's ads.
        for (const [adId, data] of adsCacheRef.current) {
            const adAdvertiserId = data?.ad?.advertiser_id ?? data?.advertiser_id ?? null
            if (adAdvertiserId === advertiserId) {
                adsCacheRef.current.delete(adId)
            }
        }
    }, [])

    const value = useMemo(() => ({
        getAd,
        getAdvertiser,
        invalidateAd,
        invalidateAdvertiser,
        feedSeed,
        setFeedSeed,
        shuffleFeedSeed,
    }), [getAd, getAdvertiser, invalidateAd, invalidateAdvertiser, feedSeed, shuffleFeedSeed])

    return <IntelligenceContext.Provider value={value}>{children}</IntelligenceContext.Provider>
}


export const useIntelligence = () => {
    const ctx = useContext(IntelligenceContext)
    if (!ctx) {
        throw new Error('useIntelligence must be used inside IntelligenceProvider')
    }
    return ctx
}
